// Potential Client Questionnaire — lead drawer panel (2026-08-29).
//
// Staff side of the questionnaire lifecycle for ONE lead:
//   • template picker + Send (admins additionally see an address override)
//   • per-questionnaire status chip (sent / in progress / submitted / expired /
//     voided) with timestamps, recipient, and checked-item progress
//   • Resend + Void (reason via useAsk) on open rows, Reopen on submitted rows
//   • the submitted view: checklist rendered as ✓ / ○ under each item label,
//     the six narratives under their prompts — ALWAYS as escaped text.
//
// Everything renders from the questionnaire's template SNAPSHOT (panel C5a /
// #20) — the live template is never consulted here.  No token or link is
// ever shown: the plaintext link exists only in the client's email (P0 #3).
//
// Realtime: listens for `questionnaire.submitted` and `questionnaire.locked`
// frames for this lead and reloads (payloads are metadata-only per panel C4 /
// #27; we refetch through the scoped API rather than trusting the frame).
//
// Questionnaire v2 (2026-10-21, docs/specs/questionnaire-v2-civil-rights.md):
//   • typed answers render as a tree (components/QuestionnaireAnswerTree.js):
//     nested follow-ups, "not asked" for follow-ups the client never saw,
//     repeating rows as a table, the contact block with an audited
//     "Apply to lead" for a detail the client changed;
//   • the files the client sent with the questionnaire (they also sit in
//     📁 Records, which owns review and legal hold for them);
//   • the answers are Rule 1.18 confidences (decision 6): the server sends
//     `answers_restricted` instead of answers to staff off the intake team —
//     the panel says why, and offers an admin the break-glass form (a POST
//     body, never a URL).  It never renders an empty answer set in its place;
//   • answers the server could not decrypt read "temporarily unavailable";
//   • the list row shows "N of M answered", files, the self-healing wrong-link
//     lock, and the erase date / legal hold;
//   • ?questionnaire=<id> on the lead URL opens that questionnaire's answers.

import { useEffect, useState, useCallback, useRef } from 'react'
import { format, formatDistanceToNow } from 'date-fns'
import {
  listQuestionnaireTemplates,
  sendLeadQuestionnaire,
  listLeadQuestionnaires,
  getLeadQuestionnaire,
  resendLeadQuestionnaire,
  voidLeadQuestionnaire,
  reopenLeadQuestionnaire,
  breakGlassLeadQuestionnaire,
  applyLeadQuestionnaireContact,
  setLeadQuestionnaireLegalHold,
  downloadLeadRecord,
} from '../services/api'
import { useToast } from '../context/ToastContext'
import { useAsk } from '../context/PromptProvider'
import QuestionnaireAnswerTree from './QuestionnaireAnswerTree'

// Chips keyed by effective_status (server-derived; 'expired' is computed on
// read from token_expires_at).  Enums only — never free text (panel #28).
export const QNR_STATUS_CHIPS = {
  sent:        { label: 'Sent',        className: 'bg-blue-100 text-blue-800' },
  in_progress: { label: 'In progress', className: 'bg-sky-100 text-sky-800' },
  submitted:   { label: 'Submitted',   className: 'bg-emerald-100 text-emerald-800' },
  expired:     { label: 'Expired',     className: 'bg-gray-100 text-gray-600' },
  voided:      { label: 'Voided',      className: 'bg-red-100 text-red-800' },
}

const NARRATIVE_KEYS = ['who', 'what', 'where', 'when', 'why', 'how']

const fmtTs = (ts) => (ts ? format(new Date(ts), 'MMM d, yyyy h:mm a') : '')
const fmtDay = (ts) => (ts ? format(new Date(ts), 'MMM d, yyyy') : '')
const fmtClock = (ts) => (ts ? format(new Date(ts), 'h:mm a') : '')
const ago = (ts) => (ts ? formatDistanceToNow(new Date(ts), { addSuffix: true }) : '')
const BREAK_GLASS_MIN = 20

function isAdminUser (u) { return u?.role === 'admin' || !!u?.permissions?.is_admin }
function fmtBytes (n) {
  const b = Number(n) || 0
  if (b < 1024) return `${b} B`
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`
  return `${(b / 1048576).toFixed(1)} MB`
}
const SCAN_CHIPS = {
  clean: { label: 'Clean', className: 'bg-emerald-100 text-emerald-800' },
  pending: { label: 'Scanning', className: 'bg-sky-100 text-sky-800' },
  infected: { label: 'Infected', className: 'bg-red-100 text-red-800' },
  error: { label: 'Unverified', className: 'bg-amber-100 text-amber-900' },
}

// Files the client sent through the questionnaire's upload step.  They are
// ordinary 📁 Records rows (same scan, encryption, retention and read log);
// review and legal hold for them live on the Records panel.
function QuestionnaireFiles ({ files, onDownload, busyId, downloadNote }) {
  if (!Array.isArray(files) || files.length === 0) return null
  return (
    <section aria-labelledby="qnr-files-h">
      <h5 id="qnr-files-h" className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Files sent with this questionnaire</h5>
      <ul className="space-y-1">
        {files.map(f => {
          const scan = SCAN_CHIPS[f.scan_status] || SCAN_CHIPS.pending
          return (
            <li key={f.id} className="bg-white border border-gray-200 rounded px-2 py-1.5 text-xs">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="font-medium text-gray-800 break-all">{f.filename}</div>
                  <div className="text-[11px] text-gray-500">{fmtBytes(f.size_bytes)} · {f.media_type}{f.uploaded_at ? ` · ${fmtTs(f.uploaded_at)}` : ''}</div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${scan.className}`}>{f.state === 'rejected' ? 'Refused' : scan.label}</span>
                  {f.purged_at && <span className="px-2 py-0.5 rounded-full text-[11px] bg-gray-200 text-gray-700">Destroyed {fmtDay(f.purged_at)}</span>}
                </div>
              </div>
              {f.screen_label && <div className={`text-[11px] mt-0.5 ${String(f.content_screen || '').startsWith('unscreened_') ? 'text-amber-800' : 'text-red-700'}`}>{f.screen_label}</div>}
              {f.downloadable && onDownload && (
                <button type="button" onClick={() => onDownload(f)} disabled={!!busyId}
                  className="mt-1 text-[11px] font-medium text-primary-700 hover:text-primary-900 disabled:opacity-40">
                  {busyId === f.id ? 'Opening…' : '⬇ Download'}
                </button>
              )}
            </li>
          )
        })}
      </ul>
      <p className="text-[11px] text-gray-400 mt-1">{downloadNote || 'Also listed in 📁 Records, where they can be marked reviewed or put on legal hold.'}</p>
    </section>
  )
}

// ─── Submitted-response renderer (shared with MatterQuestionnaireCard) ───────
// Reads ONLY the snapshot carried on the questionnaire row.  Client-supplied
// text is rendered through React's default escaping + whitespace-pre-wrap;
// it is never treated as HTML.
//
// v2: typed questions (snapshot.questions) render through QuestionnaireAnswerTree;
// the checklist and the six narratives render only when the snapshot has them
// (a v1 snapshot always does — its output is unchanged).  Staff-only actions
// come in as props: contactActions (Apply to lead), onDownloadFile.
export function QuestionnaireResponses ({ questionnaire, contactActions = null, onDownloadFile = null, downloadBusyId = null, downloadNote = null }) {
  const q = questionnaire || {}
  const snap = q.template_snapshot || {}
  const items = Array.isArray(snap.items) ? snap.items : []
  const questions = Array.isArray(snap.questions) ? snap.questions : []
  const typed = questions.length > 0
  const narrativesOn = snap.narratives_enabled !== false
  const prompts = snap.prompts || {}
  const checklist = q.checklist_state || {}
  const narratives = q.narratives || {}
  // The lead path knows the files (q.files / upload_file_count); the matter
  // path does not, so it says where they are rather than "none".
  const fileCount = Array.isArray(q.files) ? q.files.filter(f => f.state === 'admitted').length
    : q.upload_file_count != null ? Number(q.upload_file_count) || 0 : null

  const footer = (q.submitted_at || q.submitted_ip) && (
    <div className="text-[11px] text-gray-400 border-t border-gray-200 pt-2">
      Submitted {fmtTs(q.submitted_at)}
      {q.submitted_ip ? ` · from ${q.submitted_ip}` : ''}
      {q.submitted_user_agent ? <div className="truncate" title={q.submitted_user_agent}>{q.submitted_user_agent}</div> : null}
    </div>
  )
  // Fail closed and SAY so: an empty answer set would read as "the client
  // left it blank".
  if (q.answers_unavailable) {
    return (
      <div className="space-y-3">
        <div role="alert" className="text-xs bg-red-50 border border-red-200 text-red-800 rounded px-2 py-1.5">
          {q.answers_unavailable_message || 'The answers on this questionnaire are temporarily unavailable.  Nothing has been lost; ask an administrator to check the server.'}
        </div>
        {footer}
      </div>
    )
  }
  if (q.answers_purged_at || q.answers_purged) {
    return (
      <div className="space-y-3">
        <div className="text-xs bg-gray-800 text-gray-100 rounded px-2 py-1.5">
          These answers were erased{q.answers_purged_at ? ` on ${fmtDay(q.answers_purged_at)}` : ''} under the retention policy (the firm did not take the matter).  Their key was destroyed, so they cannot be read back here; the dates, counts and a deletion receipt remain.
        </div>
        {footer}
      </div>
    )
  }
  // Conditional items: one whose parent was never ticked was never PUT to this
  // client.  Rendering it as an unticked box would tell the attorney the client
  // declined to answer a question they were never shown — the opposite of what
  // happened, on a record the firm reads to decide whether to take the case.
  // `== null` covers v1 snapshots, where the key is absent entirely.
  const tick = (id) => checklist[String(id)] ?? checklist[id]
  const wasAsked = (it) => !it || it.depends_on_item_id == null || !!tick(it.depends_on_item_id)
  const askedItems = items.filter(wasAsked)
  const checkedCount = askedItems.filter(it => !!tick(it.id)).length

  return (
    <div className="space-y-4">
      {typed && (
        <QuestionnaireAnswerTree questions={questions} answers={q.answers || {}} contact={q.contact || {}}
          contactReview={Array.isArray(q.contact_review) ? q.contact_review : null} contactActions={contactActions}
          uploadNote={fileCount == null ? 'Any files sent with it are kept with the lead\'s 📁 Records.'
            : fileCount ? `${fileCount} ${fileCount === 1 ? 'file was' : 'files were'} sent${Array.isArray(q.files) ? ' — listed below' : ''}.` : 'No files were sent.'} />
      )}
      {(items.length > 0 || !typed) && (
      <div>
        <div className="flex items-center justify-between mb-1">
          <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Checklist</h5>
          <span className="text-[11px] text-gray-500">
            {checkedCount} of {askedItems.length} checked
            {askedItems.length !== items.length && ` · ${items.length - askedItems.length} not asked`}
          </span>
        </div>
        {items.length === 0 ? (
          <div className="text-xs text-gray-400 italic">This questionnaire had no checkbox items.</div>
        ) : (
          <ul className="space-y-1">
            {items.map(it => {
              const checkedAt = tick(it.id)
              const checked = !!checkedAt
              const asked = wasAsked(it)
              const isChild = it.depends_on_item_id != null
              return (
                <li key={it.id} className={`flex items-start gap-2 text-sm ${isChild ? 'ml-5' : ''}`}>
                  <span className={`flex-shrink-0 w-5 text-center ${!asked ? 'text-gray-300' : checked ? 'text-emerald-600' : 'text-gray-400'}`}
                    title={!asked ? 'Not asked — this follow-up only appears when the item above it is ticked'
                      : checked ? (typeof checkedAt === 'string' ? `Checked ${fmtTs(checkedAt)}` : 'Checked') : 'Not checked'}>
                    {!asked ? '–' : checked ? '✓' : '○'}
                  </span>
                  <span className={`min-w-0 ${!asked ? 'text-gray-400 italic' : checked ? 'text-gray-800' : 'text-gray-500'}`}>
                    <span className="break-words">{it.label}</span>
                    {it.is_required && <span className="text-red-500 ml-1" title="Required item">*</span>}
                    {!asked && <span className="text-[11px] text-gray-400 not-italic ml-1">(not asked)</span>}
                    {it.help_text && <div className="text-[11px] text-gray-400 break-words">{it.help_text}</div>}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </div>
      )}
      {narrativesOn && (
      <div>
        <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Narrative</h5>
        <div className="space-y-3">
          {NARRATIVE_KEYS.map(k => (
            <div key={k}>
              <div className="text-xs font-medium text-gray-700">{prompts[k] || k}</div>
              {narratives[k]
                ? <div className="text-sm text-gray-800 whitespace-pre-wrap break-words bg-white border border-gray-200 rounded px-2 py-1.5 mt-0.5">{narratives[k]}</div>
                : <div className="text-xs text-gray-400 italic mt-0.5">(no answer)</div>}
            </div>
          ))}
        </div>
      </div>
      )}
      <QuestionnaireFiles files={q.files} onDownload={onDownloadFile} busyId={downloadBusyId} downloadNote={downloadNote} />
      {footer}
    </div>
  )
}

// Decision 6 — the server withheld the answers.  Say why; offer an admin the
// break-glass form.  The metadata (counts, dates) stays visible.
function RestrictedAnswers ({ questionnaire, onBreakGlass, busy }) {
  const r = questionnaire.answers_restricted || {}
  const [reason, setReason] = useState('')
  const left = BREAK_GLASS_MIN - reason.trim().length
  const id = `qnr-bg-${questionnaire.id}`
  return (
    <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-2 space-y-1.5">
      <div className="font-semibold">🔒 Answers restricted</div>
      <p>{r.message}</p>
      <p className="text-amber-800">The questionnaire's status, dates and counts stay visible above.</p>
      {r.can_break_glass && (
        <div>
          <label htmlFor={id} className="block font-medium mb-0.5">Reason for opening these answers</label>
          <textarea id={id} rows={2} value={reason} onChange={e => setReason(e.target.value)} maxLength={500}
            placeholder="Why do you need a prospective client's answers you are not screened into?"
            className="w-full text-xs border border-amber-300 rounded px-2 py-1 mb-1 bg-white" />
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" disabled={left > 0 || busy} onClick={() => onBreakGlass(reason.trim())}
              className="px-2 py-1 rounded bg-amber-600 text-white disabled:opacity-40">
              {busy ? 'Opening…' : 'Open under break-glass'}
            </button>
            <span className="text-[11px] text-amber-800">{left > 0 ? `${left} more characters required` : 'This access is logged against your name and alerted.'}</span>
          </div>
        </div>
      )}
    </div>
  )
}

// "11 of 39 answered · 📎 2 files" — lead-scope metadata, no answer content.
function progressText (q) {
  const parts = []
  if (q.has_questions) parts.push(`${q.answered_count ?? 0} of ${q.question_count ?? 0} answered`)
  else if (q.item_count) parts.push(`${q.checked_count ?? 0}/${q.item_count} items`)
  if (Number(q.upload_file_count) > 0) parts.push(`📎 ${q.upload_file_count} ${Number(q.upload_file_count) === 1 ? 'file' : 'files'}`)
  return parts.join(' · ')
}

// ─── Panel ─────────────────────────────────────────────────────────────────
// `declined`: the lead's current intake decision is a decline.  The API then
// refuses send / resend / reopen (409), so the buttons are not offered either.
// `openQuestionnaireId`: open this questionnaire's answers once its row is
// here (Leads passes the ?questionnaire=<id> it read at mount).
export default function LeadQuestionnairePanel ({ leadId, lead, currentUser, initialQuestionnaires, onChanged, declined = false, openQuestionnaireId = null }) {
  const { showToast } = useToast()
  const ask = useAsk()
  const isAdmin = isAdminUser(currentUser)
  const canManage = isAdmin || !!currentUser?.permissions?.can_manage_leads

  const [rows, setRows] = useState(Array.isArray(initialQuestionnaires) ? initialQuestionnaires : [])
  const [loading, setLoading] = useState(!Array.isArray(initialQuestionnaires))
  const [error, setError] = useState('')
  const [showSend, setShowSend] = useState(false)
  const [resendTarget, setResendTarget] = useState(null)   // questionnaire row → address-override resend modal (admin)
  const [busyId, setBusyId] = useState(null)
  const [openId, setOpenId] = useState(null)               // expanded submitted row
  const [details, setDetails] = useState({})               // {qid: full questionnaire, or one carrying answers_restricted}
  // Break-glass reasons stay in memory only (never a URL, never storage):
  // apply-contact and legal-hold re-check the same gate and need it again.
  const [bgReasons, setBgReasons] = useState({})
  const [contactSel, setContactSel] = useState({})         // {qid: [contact keys ticked for Apply to lead]}
  const [actionBusy, setActionBusy] = useState('')

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const r = await listLeadQuestionnaires(leadId)
      setRows(r.data.questionnaires || [])
    } catch (err) {
      setError(err.response?.data?.error || 'Error loading questionnaires')
    } finally { setLoading(false) }
  }, [leadId])

  useEffect(() => {
    if (Array.isArray(initialQuestionnaires)) { setRows(initialQuestionnaires); setLoading(false) }
    else load()
  }, [leadId, initialQuestionnaires, load])

  // Realtime — a client submitted, or the link locked after wrong attempts.
  // Refetch through the scoped API.
  useEffect(() => {
    const handler = (e) => {
      const { type, payload } = e.detail || {}
      if ((type === 'questionnaire.submitted' || type === 'questionnaire.locked') && Number(payload?.lead_id) === Number(leadId)) load()
    }
    window.addEventListener('realtime', handler)
    return () => window.removeEventListener('realtime', handler)
  }, [leadId, load])

  const afterChange = () => { load(); if (onChanged) onChanged() }

  const openDetail = async (q) => {
    if (openId === q.id) { setOpenId(null); return }
    setOpenId(q.id)
    if (details[q.id]) return
    try {
      const r = await getLeadQuestionnaire(leadId, q.id)
      setDetails(d => ({ ...d, [q.id]: r.data.questionnaire }))
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not load responses', 'error')
      setOpenId(null)
    }
  }

  // ?questionnaire=<id> — open that questionnaire's answers once the rows are
  // here (a deep link from a notification or a colleague).  The prop wins;
  // the URL is the fallback for a panel rendered outside Leads.
  const deepLinkDone = useRef(false)
  useEffect(() => {
    if (deepLinkDone.current || loading) return
    let wanted = openQuestionnaireId
    if (wanted == null) {
      try { wanted = new URLSearchParams(window.location.search).get('questionnaire') } catch { wanted = null }
    }
    if (!wanted) { deepLinkDone.current = true; return }
    const row = rows.find(r => String(r.id) === String(wanted))
    if (!row) return
    deepLinkDone.current = true
    if ((row.effective_status || row.status) === 'submitted') openDetail(row)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, loading])

  const breakGlass = async (q, reason) => {
    setActionBusy(`bg-${q.id}`)
    try {
      const r = await breakGlassLeadQuestionnaire(leadId, q.id, reason)
      setDetails(d => ({ ...d, [q.id]: r.data.questionnaire }))
      setBgReasons(b => ({ ...b, [q.id]: reason }))
      showToast('Opened under break-glass — this access is logged and alerted.', 'warning')
    } catch (err) {
      showToast(err.response?.data?.error || err.response?.data?.message || 'Could not open the answers', 'error')
    } finally { setActionBusy('') }
  }

  const applyContact = async (q, fields) => {
    if (!fields.length) return
    setActionBusy(`apply-${q.id}`)
    const bg = bgReasons[q.id]
    try {
      if (bg) await applyLeadQuestionnaireContact(leadId, q.id, fields, { breakGlassReason: bg })
      else await applyLeadQuestionnaireContact(leadId, q.id, fields)
      showToast(`Applied to the lead: ${fields.length} detail${fields.length === 1 ? '' : 's'}`, 'success')
      // Re-read the comparison (it is against the lead as it is NOW).
      try {
        const r = bg ? await breakGlassLeadQuestionnaire(leadId, q.id, bg) : await getLeadQuestionnaire(leadId, q.id)
        setDetails(d => ({ ...d, [q.id]: r.data.questionnaire }))
      } catch { /* the toast already said it worked; the panel refreshes on reopen */ }
      if (onChanged) onChanged()
    } catch (err) {
      const d = err.response?.data || {}
      if (d.code === 'contact_not_applied' && Array.isArray(d.problems)) {
        const names = { full_name: 'name', email: 'email', phone: 'phone', city_state_zip: 'city/state' }
        showToast(`${d.error} (${d.problems.map(p => names[p.field] || p.field).join(', ')})`, 'error')
      } else showToast(d.error || d.message || 'Could not apply the details', 'error')
    } finally { setActionBusy('') }
  }

  const toggleHold = async (q) => {
    const on = !q.answers_legal_hold_at
    // Decision Q4: a lift never sets an erase date earlier than 7 days from
    // now; the server sends the date this lift would produce.
    const liftTo = q.answers_lift_purge_after
    const reason = await ask({
      type: 'textarea',
      title: on ? 'Place a legal hold on these answers' : 'Lift the legal hold',
      message: on
        ? 'A legal hold stops these answers from being erased under the retention policy.  What justifies the hold?  (At least 10 characters.)'
        : `${liftTo
          ? `After the lift, these answers will be erased on ${fmtDay(liftTo)} (never sooner than 7 days from now).`
          : 'After the lift there is still no erase date: the answers stay until the firm declines the lead or the link runs out unreturned.'}  Why are you lifting it?  (At least 10 characters.)`,
      required: true, confirmLabel: on ? 'Place hold' : 'Lift hold', danger: !on, maxLength: 500,
    })
    if (reason === null) return
    if (String(reason).trim().length < 10) { showToast('A reason of at least 10 characters is required.', 'error'); return }
    setActionBusy(`hold-${q.id}`)
    const bg = bgReasons[q.id]
    try {
      const r = bg
        ? await setLeadQuestionnaireLegalHold(leadId, q.id, on, String(reason).trim(), { breakGlassReason: bg })
        : await setLeadQuestionnaireLegalHold(leadId, q.id, on, String(reason).trim())
      const meta = r.data?.questionnaire || {}
      setDetails(d => ({ ...d, [q.id]: { ...d[q.id], ...meta, answers_legal_hold_reason: on ? String(reason).trim() : null } }))
      showToast(on ? 'Legal hold placed on the answers.'
        : meta.answers_purge_after ? `Legal hold lifted.  The answers will be erased on ${fmtDay(meta.answers_purge_after)}.` : 'Legal hold lifted.', 'success')
      load()
    } catch (err) {
      showToast(err.response?.data?.error || err.response?.data?.message || 'Could not change the legal hold', 'error')
    } finally { setActionBusy('') }
  }

  const downloadFile = async (file) => {
    setActionBusy(`dl-${file.id}`)
    try {
      const res = await downloadLeadRecord(leadId, file.id)
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `record-${file.id}-${file.filename || 'file'}`
      document.body.appendChild(a); a.click(); a.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      // responseType blob → an error body is an ArrayBuffer/Blob; decode it.
      let msg = 'Could not open that file.'
      try {
        const raw = err?.response?.data
        if (raw) {
          const text = typeof raw === 'string' ? raw : raw instanceof Blob ? await raw.text() : new TextDecoder().decode(raw)
          const parsed = JSON.parse(text)
          msg = parsed.message || parsed.error || msg
        }
      } catch { /* keep the fallback */ }
      showToast(msg, 'error')
    } finally { setActionBusy('') }
  }

  const apiError = (err, fallback) => {
    const d = err.response?.data || {}
    const status = err.response?.status
    if (status === 429) return d.error || 'Send cap reached for this lead (5 per 24 hours).'
    if (status === 409 && d.active_questionnaire_id) return d.error || 'This lead already has an active questionnaire.'
    return d.error || d.message || fallback
  }

  const resend = async (q) => {
    if (isAdmin) { setResendTarget(q); return }
    // Verifier C-1: a non-admin cannot resend to an admin-overridden address —
    // the server would 403 AFTER the confirm.  Say so up front instead.
    if (q.sent_to_override_by) { showToast('This questionnaire was sent to an admin-overridden address — ask an admin to resend it.', 'error'); return }
    const ok = await ask({ type: 'confirm', title: 'Resend questionnaire', message: `Email a fresh link to ${q.sent_to_email}?  The link gets a new address: the previous link stops working; anything the client already saved is kept.`, confirmLabel: 'Resend' })
    if (!ok) return
    setBusyId(q.id)
    try { await resendLeadQuestionnaire(leadId, q.id); showToast('Questionnaire resent', 'success'); afterChange() }
    catch (err) { showToast(apiError(err, 'Resend failed'), 'error') }
    finally { setBusyId(null) }
  }

  const voidRow = async (q) => {
    const reason = await ask({ type: 'textarea', title: 'Void questionnaire', message: 'Reason (recorded in the audit trail).  The emailed link stops working immediately.', required: true, confirmLabel: 'Void', danger: true, maxLength: 500 })
    if (reason === null) return
    if (String(reason).trim().length < 3) { showToast('Reason must be at least 3 characters', 'error'); return }
    setBusyId(q.id)
    try { await voidLeadQuestionnaire(leadId, q.id, String(reason).trim()); showToast('Questionnaire voided', 'success'); afterChange() }
    catch (err) { showToast(apiError(err, 'Void failed'), 'error') }
    finally { setBusyId(null) }
  }

  const reopen = async (q) => {
    const ok = await ask({ type: 'confirm', title: 'Reopen for changes', message: 'This keeps the original submission on the record and emails the client a NEW link pre-filled with their answers so they can add or correct information.  Continue?', confirmLabel: 'Reopen & email' })
    if (!ok) return
    setBusyId(q.id)
    try { await reopenLeadQuestionnaire(leadId, q.id, { prefill: true }); showToast('New questionnaire sent — original kept', 'success'); afterChange() }
    catch (err) { showToast(apiError(err, 'Reopen failed'), 'error') }
    finally { setBusyId(null) }
  }

  const active = rows.find(r => ['sent', 'in_progress'].includes(r.effective_status))
  const converted = !!lead?.converted_client_id
  // No new link to a person the firm declined or already converted.
  const canSendNew = canManage && !converted && !declined

  return (
    <section className="border border-gray-200 rounded-lg p-3 bg-gray-50">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider">📝 Intake questionnaire</h4>
        {canSendNew && (
          <button onClick={() => setShowSend(true)} disabled={!!active}
            title={active ? 'This lead already has an active questionnaire — resend or void it first' : 'Email the potential client a secure questionnaire link'}
            className="text-xs bg-primary-600 hover:bg-primary-700 text-white px-2 py-1 rounded disabled:opacity-40 disabled:cursor-not-allowed">
            + Send questionnaire
          </button>
        )}
      </div>
      {error && <div className="text-xs text-red-600 mb-2">{error}</div>}
      {loading ? (
        <div className="text-xs text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-gray-500 italic">
          No questionnaire sent yet.{canSendNew ? '  Send one so the potential client can answer the intake questions before anyone reviews the lead.' : ''}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {rows.map(q => {
            const st = q.effective_status || q.status
            const chip = QNR_STATUS_CHIPS[st] || { label: st, className: 'bg-gray-100 text-gray-700' }
            const isOpen = ['sent', 'in_progress', 'expired'].includes(st)
            const busy = busyId === q.id
            const expanded = openId === q.id
            return (
              <li key={q.id} className="bg-white border border-gray-200 rounded px-2 py-1.5 text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${chip.className}`}>{chip.label}</span>
                  <span className="text-gray-700 truncate flex-1" title={q.template_name || ''}>
                    {q.template_name || 'Questionnaire'}
                    {q.supersedes_id ? <span className="text-gray-400"> · reopened from #{q.supersedes_id}</span> : null}
                  </span>
                  {(st === 'in_progress' || (st === 'submitted' && q.has_questions)) && progressText(q) && (
                    <span className="text-gray-500">{progressText(q)}</span>
                  )}
                  {st === 'submitted' && (
                    <button onClick={() => openDetail(q)} className="text-primary-600 hover:text-primary-800 font-medium">
                      {expanded ? 'Hide responses' : 'View responses'}
                    </button>
                  )}
                  {canManage && isOpen && (
                    <>
                      {!declined && (
                        <button onClick={() => resend(q)} disabled={busy} className="text-primary-600 hover:text-primary-800 disabled:opacity-40">↻ Resend</button>
                      )}
                      <button onClick={() => voidRow(q)} disabled={busy} className="text-red-600 hover:text-red-800 disabled:opacity-40">✕ Void</button>
                    </>
                  )}
                  {canSendNew && st === 'submitted' && !active && (
                    <button onClick={() => reopen(q)} disabled={busy} className="text-amber-700 hover:text-amber-900 disabled:opacity-40" title="Send a fresh, pre-filled link so the client can add or correct information.  The original submission is kept.">⟲ Reopen</button>
                  )}
                </div>
                <div className="text-gray-500 mt-0.5 space-x-2">
                  <span>To {q.sent_to_email}{q.sent_to_override_by ? <span className="text-amber-700" title="Address overridden by an admin"> (override)</span> : null}</span>
                  <span>· sent {fmtTs(q.sent_at)}{q.sent_by_name ? ` by ${q.sent_by_name}` : ''}</span>
                  {q.resent_at && <span>· resent {ago(q.resent_at)}</span>}
                  {q.require_email_code && <span className="text-gray-400" title="The client must also enter a 6-digit code emailed to them (the firm setting, or this questionnaire's template, requires it)">· email code required</span>}
                </div>
                <div className="text-gray-500 mt-0.5 space-x-2">
                  {q.viewed_at && <span>Opened {ago(q.viewed_at)}</span>}
                  {q.last_saved_at && !q.submitted_at && <span>· last saved {ago(q.last_saved_at)}</span>}
                  {q.submitted_at && <span className="text-emerald-700">Submitted {fmtTs(q.submitted_at)}</span>}
                  {st === 'expired' && q.token_expires_at && <span>Link expired {ago(q.token_expires_at)} — resend to issue a new one</span>}
                  {['sent', 'in_progress'].includes(st) && q.token_expires_at && <span>· link expires {ago(q.token_expires_at)}</span>}
                  {st === 'voided' && <span className="text-red-700">Voided {fmtTs(q.voided_at)}{q.void_reason ? ` — ${q.void_reason}` : ''}</span>}
                  {q.matter_id && <span>· linked to matter #{q.matter_id}</span>}
                </div>
                {(q.locked_until || q.answers_purge_after || q.answers_purged_at || q.answers_legal_hold_at) && (
                  <div className="mt-0.5 space-x-2">
                    {q.locked_until && (
                      <span className="text-amber-800" title="Ten wrong link attempts lock the questionnaire for 15 minutes.  It unlocks by itself; ↻ Resend unlocks it now.  The client's saved answers are kept.">
                        🔒 Locked after wrong link attempts — unlocks by itself at {fmtClock(q.locked_until)}{canManage && !declined ? ' (↻ Resend unlocks it now)' : ''}
                      </span>
                    )}
                    {q.answers_purged_at
                      ? <span className="text-gray-600">Answers erased {fmtDay(q.answers_purged_at)}</span>
                      : q.answers_legal_hold_at
                        ? <span className="text-purple-800">⚖ Answers on legal hold</span>
                        : q.answers_purge_after && <span className="text-gray-600" title="The firm did not take the matter (or the link was never returned): the answers are erased after the retention period unless a legal hold is placed.">Answers will be erased on {fmtDay(q.answers_purge_after)}</span>}
                  </div>
                )}
                {expanded && (
                  <div className="mt-2 border-t border-gray-200 pt-2">
                    {!details[q.id]
                      ? <div className="text-gray-500">Loading responses…</div>
                      : details[q.id].answers_restricted
                        ? <RestrictedAnswers questionnaire={{ ...q, ...details[q.id] }} busy={actionBusy === `bg-${q.id}`} onBreakGlass={(reason) => breakGlass(q, reason)} />
                        : (
                          <div className="space-y-2">
                            {details[q.id].answers_access?.break_glass && (
                              <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1">Opened under break-glass — logged against your name.</div>
                            )}
                            <QuestionnaireResponses questionnaire={details[q.id]}
                              contactActions={canManage && details[q.id].status === 'submitted' ? {
                                selected: contactSel[q.id] || null,
                                onSelect: (keys) => setContactSel(s => ({ ...s, [q.id]: keys })),
                                onApply: (keys) => applyContact(q, keys),
                                busy: actionBusy === `apply-${q.id}`,
                              } : null}
                              onDownloadFile={details[q.id].answers_access?.break_glass ? null : downloadFile}
                              downloadBusyId={actionBusy.startsWith('dl-') ? Number(actionBusy.slice(3)) : null}
                              downloadNote={details[q.id].answers_access?.break_glass ? 'Download these from 📁 Records (it asks for its own break-glass reason).' : null} />
                            {isAdmin && !details[q.id].answers_unavailable && !details[q.id].answers_purged_at && (
                              <div className="flex items-center gap-2 flex-wrap text-[11px] border-t border-gray-200 pt-2">
                                {details[q.id].answers_legal_hold_at
                                  ? <span className="text-purple-800">⚖ On legal hold since {fmtDay(details[q.id].answers_legal_hold_at)}{details[q.id].answers_legal_hold_reason ? ` — ${details[q.id].answers_legal_hold_reason}` : ''}</span>
                                  : details[q.id].answers_purge_after
                                    ? <span className="text-gray-600">Answers will be erased on {fmtDay(details[q.id].answers_purge_after)}.</span>
                                    : <span className="text-gray-500">No erase date is set.</span>}
                                <button type="button" onClick={() => toggleHold(details[q.id])} disabled={!!actionBusy}
                                  className="font-medium text-purple-700 hover:text-purple-900 disabled:opacity-40">
                                  {actionBusy === `hold-${q.id}` ? 'Saving…' : details[q.id].answers_legal_hold_at ? 'Lift legal hold' : '⚖ Legal hold on answers'}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {showSend && (
        <SendQuestionnaireModal
          leadId={leadId} lead={lead} isAdmin={isAdmin}
          onClose={() => setShowSend(false)}
          onSent={() => { setShowSend(false); showToast('Questionnaire sent', 'success'); afterChange() }} />
      )}
      {resendTarget && (
        <SendQuestionnaireModal
          leadId={leadId} lead={lead} isAdmin={isAdmin} resendOf={resendTarget}
          onClose={() => setResendTarget(null)}
          onSent={() => { setResendTarget(null); showToast('Questionnaire resent', 'success'); afterChange() }} />
      )}
    </section>
  )
}

// ─── Send / resend modal ───────────────────────────────────────────────────
// Non-admins see the recipient as a fixed label (the lead's email on file —
// the server refuses any other address for them).  Admins may override the
// address with a required reason; the override is audited server-side.
function SendQuestionnaireModal ({ leadId, lead, isAdmin, resendOf, onClose, onSent }) {
  const [templates, setTemplates] = useState([])
  const [templateId, setTemplateId] = useState('')
  const [loading, setLoading] = useState(!resendOf)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const leadEmail = String(lead?.email || '').trim()
  // Verifier C-1: when RESENDING a questionnaire that was originally sent to
  // an admin-overridden address, start the modal IN override mode showing the
  // real target — the server refuses an overridden resend without a fresh
  // reason, and the old UI showed the lead's email while the server would
  // have targeted the override address (400 dead-end).
  const wasOverridden = !!resendOf?.sent_to_override_by
    && String(resendOf?.sent_to_email || '').trim().toLowerCase() !== leadEmail.toLowerCase()
  const [override, setOverride] = useState(wasOverridden)
  const [toEmail, setToEmail] = useState(resendOf?.sent_to_email || leadEmail)
  const [overrideReason, setOverrideReason] = useState('')

  useEffect(() => {
    if (resendOf) return
    let cancelled = false
    listQuestionnaireTemplates().then(r => {
      if (cancelled) return
      const list = (r.data.templates || []).filter(t => t.is_active !== false)
      setTemplates(list)
      if (list.length === 1) setTemplateId(String(list[0].id))
    }).catch(err => { if (!cancelled) setError(err.response?.data?.error || 'Could not load questionnaire templates') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [resendOf])

  const effectiveTo = override ? toEmail.trim() : leadEmail
  const differs = effectiveTo.toLowerCase() !== leadEmail.toLowerCase()
  // Decision Q2: the client's SAVED answers travel with this link, so sending
  // them anywhere but the lead's email on file is a break-glass action.
  const carriesAnswers = !!resendOf?.has_saved_answers
  const breakGlass = differs && carriesAnswers
  const minReason = breakGlass ? BREAK_GLASS_MIN : 3

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    if (!resendOf && !templateId) { setError('Pick a questionnaire template'); return }
    if (!effectiveTo) { setError('This lead has no email on file.  Add one to the lead first' + (isAdmin ? ', or use the address override.' : '.')); return }
    if (differs && overrideReason.trim().length < minReason) {
      setError(breakGlass
        ? `This questionnaire carries the client's saved answers.  A break-glass reason of at least ${BREAK_GLASS_MIN} characters is required to send it to a different address.`
        : 'An override reason is required when sending to a different address.')
      return
    }
    setSaving(true)
    try {
      const body = {}
      if (differs) { body.to_email = effectiveTo; body.override_reason = overrideReason.trim() }
      if (resendOf) await resendLeadQuestionnaire(leadId, resendOf.id, body)
      else await sendLeadQuestionnaire(leadId, { template_id: parseInt(templateId, 10), ...body })
      onSent()
    } catch (err) {
      const d = err.response?.data || {}
      setError(d.error || d.message || 'Send failed')
    } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-4">
        <h3 className="text-base font-bold">{resendOf ? 'Resend questionnaire' : 'Send intake questionnaire'}</h3>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-sm">{error}</div>}
        {!resendOf && (
          <label className="block text-sm">
            <span className="text-gray-700 font-medium">Questionnaire template</span>
            {loading ? (
              <div className="text-xs text-gray-500 mt-1">Loading templates…</div>
            ) : templates.length === 0 ? (
              <div className="text-xs text-amber-700 mt-1">No active questionnaire templates.  An admin can build one on the Intake Forms page → Questionnaire templates.</div>
            ) : (
              <select value={templateId} onChange={e => setTemplateId(e.target.value)} required
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Select…</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {Number(t.question_count) > 0
                      ? ` (${t.question_count} question${Number(t.question_count) === 1 ? '' : 's'})`
                      : t.item_count != null ? ` (${t.item_count} item${t.item_count === 1 ? '' : 's'})` : ''}
                  </option>
                ))}
              </select>
            )}
          </label>
        )}
        <div className="text-sm">
          <div className="text-gray-700 font-medium">Recipient</div>
          {wasOverridden && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
              Originally sent to an admin-overridden address.  Resending there requires a fresh override reason (audited); or switch back to the lead's email.
            </div>
          )}
          {!override ? (
            <div className="mt-1 flex items-center gap-2">
              <span className="text-gray-800">{leadEmail || <em className="text-amber-700">no email on file</em>}</span>
              {isAdmin && <button type="button" onClick={() => setOverride(true)} className="text-xs text-primary-600 hover:text-primary-800">Send to a different address…</button>}
            </div>
          ) : (
            <div className="mt-1 space-y-2">
              <input type="email" value={toEmail} onChange={e => setToEmail(e.target.value)} placeholder="name@example.com"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              {breakGlass && (
                <div role="note" className="text-[11px] text-amber-900 bg-amber-50 border border-amber-300 rounded px-2 py-1">
                  ⚠ This questionnaire carries the client's saved answers, and whoever opens the link sees them.  Sending it to an address other than the lead's email on file <strong>breaks glass</strong> — give a reason of at least {BREAK_GLASS_MIN} characters.  It is logged against your name and alerted.
                </div>
              )}
              <label htmlFor="qnr-override-reason" className="sr-only">{breakGlass ? 'Break-glass reason' : 'Override reason'}</label>
              <textarea id="qnr-override-reason" value={overrideReason} onChange={e => setOverrideReason(e.target.value)} rows={2} maxLength={500}
                placeholder={breakGlass ? `Break-glass reason — why send the client's saved answers to this address?  (at least ${BREAK_GLASS_MIN} characters, logged and alerted)` : 'Why a different address?  (required, audited)'}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              {breakGlass && overrideReason.trim().length < BREAK_GLASS_MIN && (
                <div className="text-[11px] text-amber-800">{BREAK_GLASS_MIN - overrideReason.trim().length} more characters required</div>
              )}
              <button type="button" onClick={() => { setOverride(false); setToEmail(leadEmail) }} className="text-xs text-gray-500 hover:text-gray-700">Use the lead's email instead</button>
            </div>
          )}
          {!isAdmin && <div className="text-[11px] text-gray-400 mt-1">Only an admin can send to an address other than the lead's email on file.</div>}
        </div>
        <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-2">
          One email goes to the recipient with a personal link that expires in 14 days.  {resendOf ? 'The link gets a new address: the previous link stops working; saved progress is kept.  ' : ''}The link is never shown here — it exists only in that email.
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button type="submit" disabled={saving || (!resendOf && (loading || templates.length === 0))}
            className="px-4 py-2 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50">
            {saving ? 'Sending…' : (resendOf ? 'Resend' : 'Send')}
          </button>
        </div>
      </form>
    </div>
  )
}
